import { useState } from "react";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  IE_COMPLAINT_EXPORT_COLUMNS,
  IE_COMPLAINT_HISTORY_COLUMNS,
  getExportDateTime,
  getHistoryForRow,
  loadAllIeComplaintHistory,
  mapComplaintRowsForExport,
  mapHistoryRowsForExport,
} from "./IeComplaintExportHelpers";
import { useIeComplaintStore } from "./IeComplaintStore/useIeComplaintStore";
import ExportMenu from "../../../Common/ExportMenu/ExportMenu";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "003366" },
};

const HISTORY_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "003366" },
};

const MAIN_HEAD_STYLES = {
  fillColor: [0, 51, 102],
  textColor: [255, 255, 255],
  fontSize: 7,
  halign: "center",
};

const MAIN_BODY_STYLES = {
  fontSize: 6,
  halign: "center",
};

const HISTORY_HEAD_STYLES = {
  fillColor: [0, 51, 102],
  textColor: [255, 255, 255],
  fontSize: 7,
  halign: "center",
};

const HISTORY_BODY_STYLES = {
  fontSize: 6,
  halign: "center",
};

const centerCell = (cell) => {
  cell.alignment = { horizontal: "center" };
};

const styleHeaderRow = (row, fill, columnCount) => {
  const boldFont = { bold: true, color: { argb: "FFFFFFFF" } };
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = boldFont;
    centerCell(cell);
    if (colNumber <= columnCount) {
      cell.fill = fill;
    }
  });
};

// Explicit widths (mm) for IE_COMPLAINT_EXPORT_COLUMNS / mainRows columns, sized to fit
// a landscape page (269mm usable width) with room for each column's full header text
// on one or two lines — narrower widths forced autoTable into vertical letter-by-letter
// wrapping for the longer headers (e.g. "Complianant Contact No", "Mode Of Reporting").
const MAIN_COLUMN_STYLES = {
  0: { cellWidth: 10 }, // S.No
  1: { cellWidth: 18 }, // Complaint No
  2: { cellWidth: 20 }, // Complianant Contact No
  3: { cellWidth: 16 }, // KPI Category
  4: { cellWidth: 14 }, // Issue Type
  5: { cellWidth: 26 }, // Description
  6: { cellWidth: 12 }, // Zone
  7: { cellWidth: 10 }, // Ward
  8: { cellWidth: 14 }, // Location
  9: { cellWidth: 18 }, // Complaint Created On
  10: { cellWidth: 18 }, // Complaint Closure On
  11: { cellWidth: 20 }, // Actual Duration (HH:MM)
  12: { cellWidth: 14 }, // Colony Name
  13: { cellWidth: 12 }, // Status
  14: { cellWidth: 18 }, // Mode Of Reporting
  15: { cellWidth: 26 }, // Action Remarks
};

// Explicit widths (mm) for IE_COMPLAINT_HISTORY_COLUMNS, sized to fit the
// indented history sub-table (261mm usable width after the 22mm left margin).
const HISTORY_COLUMN_STYLES = {
  0: { cellWidth: 12 }, // S.No
  1: { cellWidth: 42 }, // Action Taken By
  2: { cellWidth: 22 }, // Status
  3: { cellWidth: 84 }, // Action Remarks
  4: { cellWidth: 42 }, // Assigned On
  5: { cellWidth: 42 }, // Action Taken On
};

const getStartY = (doc) => doc.lastAutoTable?.finalY ?? 35;

const addPageIfNeeded = (doc, startY, minSpace = 30) => {
  const pageHeight = doc.internal.pageSize.height;
  if (startY + minSpace > pageHeight - 14) {
    doc.addPage();
    return 20;
  }
  return startY;
};

export const ExportIeComplaint = ({ complaintData = [] }) => {
  const fetchActionHistory = useIeComplaintStore((state) => state.fetchActionHistory);
  const fetchAllComplaintsForExport = useIeComplaintStore(
    (state) => state.fetchAllComplaintsForExport
  );
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const loadAllHistory = (data) => loadAllIeComplaintHistory(data, fetchActionHistory);

  const exportToExcel = async () => {
    setIsExportingExcel(true);

    try {
      const allData = await fetchAllComplaintsForExport();
      const historyById = await loadAllHistory(allData);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("IE Complaint List");
      const boldFont = { bold: true };
      const mainColumnCount = IE_COMPLAINT_EXPORT_COLUMNS.length;
      const historyColumnCount = IE_COMPLAINT_HISTORY_COLUMNS.length;

      worksheet.addRow(["", "", getExportDateTime(), ""]);
      worksheet.mergeCells(1, 2, 1, mainColumnCount);
      worksheet.getCell(1, 2).value = getExportDateTime();
      worksheet.getCell(1, 2).font = boldFont;
      worksheet.getCell(1, 2).alignment = { horizontal: "center" };

      worksheet.addRow(["", "", "IE Complaint List", ""]);
      worksheet.mergeCells(2, 2, 2, mainColumnCount);
      worksheet.getCell(2, 2).value = "IE Complaint List";
      worksheet.getCell(2, 2).font = boldFont;
      worksheet.getCell(2, 2).alignment = { horizontal: "center" };

      const mainHeaderRow = worksheet.addRow(IE_COMPLAINT_EXPORT_COLUMNS);
      styleHeaderRow(mainHeaderRow, HEADER_FILL, mainColumnCount);

      const mainRows = mapComplaintRowsForExport(allData);
      mainRows.forEach((mainRow, index) => {
        const complaint = allData[index];
        const history = getHistoryForRow(complaint, historyById);

        worksheet.addRow(mainRow).eachCell({ includeEmpty: true }, centerCell);

        if (!history.length) return;

        const historyTitleRow = worksheet.addRow([
          "",
          `Action History - ${complaint.complainantNo || "N/A"}`,
        ]);
        worksheet.mergeCells(
          historyTitleRow.number,
          2,
          historyTitleRow.number,
          1 + historyColumnCount
        );
        const titleCell = historyTitleRow.getCell(2);
        titleCell.font = boldFont;
        titleCell.fill = HISTORY_HEADER_FILL;

        const historyHeaderRow = worksheet.addRow(["", ...IE_COMPLAINT_HISTORY_COLUMNS]);
        styleHeaderRow(historyHeaderRow, HISTORY_HEADER_FILL, historyColumnCount + 1);

        mapHistoryRowsForExport(history).forEach((historyRow) => {
          worksheet
            .addRow(["", ...historyRow])
            .eachCell({ includeEmpty: true }, centerCell);
        });

        worksheet.addRow([]);
      });

      worksheet.columns = [
        { width: 8 }, // S.No
        { width: 18 }, // Complaint No
        { width: 22 }, // Complianant Contact No
        { width: 18 }, // KPI Category
        { width: 16 }, // Issue Type
        { width: 45 }, // Description
        { width: 14 }, // Zone
        { width: 10 }, // Ward
        { width: 20 }, // Location
        { width: 20 }, // Complaint Created On
        { width: 20 }, // Complaint Closure On
        { width: 18 }, // Actual Duration (HH:MM)
        { width: 18 }, // Colony Name
        { width: 12 }, // Status
        { width: 16 }, // Mode Of Reporting
        { width: 40 }, // Action Remarks
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "IEComplaintList.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const exportToPDF = async () => {
    setIsExportingPdf(true);

    try {
      const allData = await fetchAllComplaintsForExport();
      const historyById = await loadAllHistory(allData);
      const mainRows = mapComplaintRowsForExport(allData);
      const doc = new jsPDF({ orientation: "landscape" });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("IE Complaint List", pageWidth / 2, 20, { align: "center" });
      doc.setFontSize(10);
      doc.text(getExportDateTime(), pageWidth / 2, 28, { align: "center" });

      let startY = 35;

      mainRows.forEach((mainRow, index) => {
        const complaint = allData[index];
        const history = getHistoryForRow(complaint, historyById);

        startY = addPageIfNeeded(doc, startY, 12);

        autoTable(doc, {
          startY,
          head: index === 0 ? [IE_COMPLAINT_EXPORT_COLUMNS] : [],
          body: [mainRow],
          headStyles: MAIN_HEAD_STYLES,
          bodyStyles: MAIN_BODY_STYLES,
          columnStyles: MAIN_COLUMN_STYLES,
          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: "linebreak",
            valign: "middle",
          },
          margin: { left: 14, right: 14 },
        });

        startY = getStartY(doc) + 4;

        if (!history.length) return;

        startY = addPageIfNeeded(doc, startY, 24);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setFillColor(219, 234, 254);
        doc.rect(14, startY - 5, pageWidth - 28, 8, "F");
        doc.setTextColor(0, 51, 102);
        doc.text(`Action History - ${complaint.complainantNo || "N/A"}`, 18, startY);
        doc.setTextColor(0, 0, 0);

        autoTable(doc, {
          startY: startY + 3,
          head: [IE_COMPLAINT_HISTORY_COLUMNS],
          body: mapHistoryRowsForExport(history),
          headStyles: HISTORY_HEAD_STYLES,
          bodyStyles: HISTORY_BODY_STYLES,
          columnStyles: HISTORY_COLUMN_STYLES,
          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: "linebreak",
            valign: "middle",
          },
          margin: { left: 22, right: 14 },
        });

        startY = getStartY(doc) + 8;
      });

      doc.save("IEComplaintList.pdf");
    } catch (error) {
      console.error("Error generating PDF file:", error);
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div className="ms-3">
      <ExportMenu
        items={[
          {
            label: "Excel",
            icon: "fa-solid fa-file-excel",
            onClick: exportToExcel,
            loading: isExportingExcel,
            disabled: isExportingPdf,
          },
          {
            label: "PDF",
            icon: "fa-solid fa-file-pdf",
            onClick: exportToPDF,
            loading: isExportingPdf,
            disabled: isExportingExcel,
          },
        ]}
      />
    </div>
  );
};

// export const ExportIeComplaint = ({ complaintData = [] }) => {
//   const fetchActionHistory = useIeComplaintStore((state) => state.fetchActionHistory);
//   const [isExporting, setIsExporting] = useState(false);

//   const loadAllHistory = () => loadAllIeComplaintHistory(complaintData, fetchActionHistory);

//   const exportToExcel = async () => {
//     setIsExporting(true);

//     try {
//       const historyById = await loadAllHistory();
//       const workbook = new ExcelJS.Workbook();
//       const worksheet = workbook.addWorksheet("IE Complaint List");
//       const boldFont = { bold: true };
//       const mainColumnCount = IE_COMPLAINT_EXPORT_COLUMNS.length;
//       const historyColumnCount = IE_COMPLAINT_HISTORY_COLUMNS.length;

//       worksheet.addRow(["", "", getExportDateTime(), ""]);
//       worksheet.mergeCells(1, 2, 1, mainColumnCount);
//       worksheet.getCell(1, 2).font = boldFont;
//       worksheet.getCell(1, 2).alignment = { horizontal: "center" };

//       worksheet.addRow(["", "", "IE Complaint List", ""]);
//       worksheet.mergeCells(2, 2, 2, mainColumnCount);
//       worksheet.getCell(2, 2).font = boldFont;
//       worksheet.getCell(2, 2).alignment = { horizontal: "center" };

//       const mainHeaderRow = worksheet.addRow(IE_COMPLAINT_EXPORT_COLUMNS);
//       styleHeaderRow(mainHeaderRow, HEADER_FILL, mainColumnCount);

//       const mainRows = mapComplaintRowsForExport(complaintData);
//       mainRows.forEach((mainRow, index) => {
//         const complaint = complaintData[index];
//         const history = getHistoryForRow(complaint, historyById);

//         worksheet.addRow(mainRow).eachCell({ includeEmpty: true }, centerCell);

//         if (!history.length) return;

//         const historyTitleRow = worksheet.addRow([
//           "",
//           `Action History - ${complaint.complainantNo || "N/A"}`,
//         ]);
//         worksheet.mergeCells(
//           historyTitleRow.number,
//           2,
//           historyTitleRow.number,
//           1 + historyColumnCount
//         );
//         const titleCell = historyTitleRow.getCell(2);
//         titleCell.font = boldFont;
//         titleCell.fill = HISTORY_HEADER_FILL;

//         const historyHeaderRow = worksheet.addRow(["", ...IE_COMPLAINT_HISTORY_COLUMNS]);
//         styleHeaderRow(historyHeaderRow, HISTORY_HEADER_FILL, historyColumnCount + 1);

//         mapHistoryRowsForExport(history).forEach((historyRow) => {
//           worksheet
//             .addRow(["", ...historyRow])
//             .eachCell({ includeEmpty: true }, centerCell);
//         });

//         worksheet.addRow([]);
//       });

//       const buffer = await workbook.xlsx.writeBuffer();
//       const blob = new Blob([buffer], {
//         type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//       });
//       const url = URL.createObjectURL(blob);
//       const link = document.createElement("a");
//       link.href = url;
//       link.download = "IEComplaintList.xlsx";
//       link.click();
//       URL.revokeObjectURL(url);
//     } catch (error) {
//       console.error("Error generating Excel file:", error);
//     } finally {
//       setIsExporting(false);
//     }
//   };

//   const exportToPDF = async () => {
//     setIsExporting(true);

//     try {
//       const historyById = await loadAllHistory();
//       const mainRows = mapComplaintRowsForExport(complaintData);
//       const doc = new jsPDF({ orientation: "landscape" });
//       const pageWidth = doc.internal.pageSize.width;

//       doc.setFont("helvetica", "bold");
//       doc.setFontSize(16);
//       doc.text("IE Complaint List", pageWidth / 2, 20, { align: "center" });
//       doc.setFontSize(10);
//       doc.text(getExportDateTime(), pageWidth / 2, 28, { align: "center" });

//       let startY = 35;

//       mainRows.forEach((mainRow, index) => {
//         const complaint = complaintData[index];
//         const history = getHistoryForRow(complaint, historyById);

//         startY = addPageIfNeeded(doc, startY, 12);

//         autoTable(doc, {
//           startY,
//           head: index === 0 ? [IE_COMPLAINT_EXPORT_COLUMNS] : [],
//           body: [mainRow],
//           headStyles: MAIN_HEAD_STYLES,
//           bodyStyles: MAIN_BODY_STYLES,
//           margin: { left: 14, right: 14 },
//         });

//         startY = getStartY(doc) + 4;

//         if (!history.length) return;

//         startY = addPageIfNeeded(doc, startY, 24);

//         doc.setFont("helvetica", "bold");
//         doc.setFontSize(9);
//         doc.setFillColor(219, 234, 254);
//         doc.rect(14, startY - 5, pageWidth - 28, 8, "F");
//         doc.setTextColor(0, 51, 102);
//         doc.text(`Action History - ${complaint.complainantNo || "N/A"}`, 18, startY);
//         doc.setTextColor(0, 0, 0);

//         autoTable(doc, {
//           startY: startY + 3,
//           head: [IE_COMPLAINT_HISTORY_COLUMNS],
//           body: mapHistoryRowsForExport(history),
//           headStyles: HISTORY_HEAD_STYLES,
//           bodyStyles: HISTORY_BODY_STYLES,
//           margin: { left: 22, right: 14 },
//         });

//         startY = getStartY(doc) + 8;
//       });

//       doc.save("IEComplaintList.pdf");
//     } catch (error) {
//       console.error("Error generating PDF file:", error);
//     } finally {
//       setIsExporting(false);
//     }
//   };

//   return (
//     <>
//       <button
//         type="button"
//         onClick={exportToExcel}
//         className="button-container ms-3"
//         disabled={isExporting}
//       >
//         <i className="fa-solid fa-file-excel me-1 icon-container"></i>
//         {isExporting ? "Exporting..." : "Excel"}
//       </button>
//       <button
//         type="button"
//         onClick={exportToPDF}
//         className="button-container ms-3"
//         disabled={isExporting}
//       >
//         <i className="fa-solid fa-file-pdf me-1 icon-container"></i>
//         {isExporting ? "" : "PDF"}
//       </button>
//     </>
//   );
// };
